// Shared low-poly tree parts (temperate & cold) — a deterministic jitter stream
// (never Math.random, so a variant always rebuilds byte-identical geometry), a
// flattened leaf-lobe primitive the layered broadleaf canopies stack, and a
// conifer foliage-tier primitive with a per-tier off-axis nudge and spin so a
// stacked crown never reads lathe-perfect. Lobes and tiers spin about Y only,
// so a part's top height stays exact. Ported from age-of-mcp's v2/tree-parts.ts:
// the pure geometry is kept verbatim, with the Part type taken from the library.

import * as THREE from "three";
import type { Part } from "../types";

/** Deterministic 0..1 hash stream keyed by (seed, n). */
export function h01(seed: number, n: number): number {
  const x = Math.sin(seed * 113.9 + n * 271.3 + 59.2) * 43758.5453;
  return x - Math.floor(x);
}

/** Linear blend between `a` and `b`. */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Pick an entry by variant, wrapping safely for any v (even negative). */
export function pick<T>(arr: readonly T[], v: number): T {
  return arr[((v % arr.length) + arr.length) % arr.length]!;
}

/**
 * One canopy leaf lobe — a flattened icosahedron with a deterministic
 * yaw-only spin (rotation about Y never changes a vertex's height, so the
 * lobe's top stays exactly y + r * squash * 0.851 and canopy heights are
 * predictable). `squash` < 1 flattens it into the cloud-layer look the
 * broadleaf canopies are built from; `stretch` widens it along x.
 */
export function lobe(
  seed: number, n: number,
  x: number, y: number, z: number,
  r: number, color: string, squash = 0.75, stretch = 1,
): Part {
  return {
    geometry: new THREE.IcosahedronGeometry(r, 0),
    color,
    position: [x, y, z],
    rotation: [0, h01(seed, n) * Math.PI * 2, 0],
    scale: [stretch, squash, 1],
  };
}

/**
 * One conifer foliage tier — a low-segment cone nudged a deterministic
 * distance off-axis and spun about Y, so a stack of tiers reads shingled
 * and slightly irregular. The tier's top stays exactly y + h / 2.
 */
export function tier(
  seed: number, n: number,
  y: number, r: number, h: number, color: string,
  segments = 6, offset = 0.03,
): Part {
  const ang = h01(seed, n) * Math.PI * 2;
  const d = offset * h01(seed, n + 1);
  return {
    geometry: new THREE.ConeGeometry(r, h, segments),
    color,
    position: [Math.cos(ang) * d, y, Math.sin(ang) * d],
    rotation: [0, h01(seed, n + 2) * Math.PI, 0],
  };
}
